import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { 
  Building2, 
  Plus, 
  Search, 
  Filter, 
  MoreVertical,
  CheckCircle,
  AlertCircle,
  Clock,
  XCircle,
  RefreshCw,
  Globe,
  Users,
  HardDrive,
  Activity
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import CosmicButton from '../ui/CosmicButton';
import CosmicCard from '../ui/CosmicCard';
import CosmicInput from '../ui/CosmicInput';
import LoadingSpinner from '../ui/LoadingSpinner';
import api from '@/services/api';
import type { ApiResponse } from '@/types';

interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  custom_domain?: string;
  plan_type: 'basic' | 'pro' | 'enterprise';
  provisioning_status: 'pending' | 'in_progress' | 'completed' | 'failed';
  status: 'active' | 'suspended' | 'deleted';
  created_at: string;
  provisioned_at?: string;
  resource_usage?: {
    storage_gb: number;
    users: number;
    api_calls: number;
  };
  limits?: {
    storage_gb: number;
    users: number;
    api_calls_per_month: number;
  };
}

interface ProvisioningJob {
  id: string;
  tenant_id: string;
  status: string;
  steps: Array<{
    name: string;
    status: 'pending' | 'in_progress' | 'completed' | 'failed';
    started_at?: string;
    completed_at?: string;
    error?: string;
  }>;
  created_at: string;
  completed_at?: string;
}

const statusIcons = {
  pending: <Clock className="w-5 h-5 text-yellow-500" />,
  in_progress: <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />,
  completed: <CheckCircle className="w-5 h-5 text-green-500" />,
  failed: <XCircle className="w-5 h-5 text-red-500" />,
  active: <CheckCircle className="w-5 h-5 text-green-500" />,
  suspended: <AlertCircle className="w-5 h-5 text-yellow-500" />
};

const planColors = {
  basic: 'bg-gray-100 text-gray-800',
  pro: 'bg-blue-100 text-blue-800',
  enterprise: 'bg-purple-100 text-purple-800'
};

export const TenantManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [showProvisioningWizard, setShowProvisioningWizard] = useState(false);
  const queryClient = useQueryClient();

  // Fetch tenants
  const { data: tenants, isLoading, refetch } = useQuery<Tenant[]>({
    queryKey: ['admin-tenants', statusFilter, searchTerm],
    queryFn: async () => {
      const res = await api.get<ApiResponse<Tenant[]>>('/admin/tenants', { 
        params: { 
          status: statusFilter !== 'all' ? statusFilter : undefined,
          search: searchTerm || undefined
        }
      });
      return res.data.data || [];
    },
    refetchInterval: 5000 // Refetch every 5 seconds for real-time updates
  });

  // Fetch active provisioning jobs
  const { data: provisioningJobs } = useQuery<ProvisioningJob[]>({
    queryKey: ['provisioning-jobs'],
    queryFn: async () => {
      const res = await api.get<ApiResponse<ProvisioningJob[]>>('/admin/tenants/provisioning-jobs');
      return res.data.data || [];
    },
    refetchInterval: 2000, // More frequent updates for provisioning status
    enabled: tenants?.some((t: Tenant) => t.provisioning_status === 'in_progress')
  });

  // Bulk actions mutation
  const bulkActionMutation = useMutation({
    mutationFn: ({ action, tenantIds }: { action: string; tenantIds: string[] }) =>
      api.post(`/admin/tenants/bulk-${action}`, { tenantIds }),
    onSuccess: () => {
      toast.success('Bulk action completed successfully');
      setSelectedTenants([]);
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Bulk action failed');
    }
  });

  const filteredTenants = tenants?.filter((tenant: Tenant) => {
    const matchesSearch = searchTerm ? 
      tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.subdomain.toLowerCase().includes(searchTerm.toLowerCase()) : true;
    return matchesSearch;
  }) || [];

  const handleSelectAll = () => {
    if (selectedTenants.length === filteredTenants.length) {
      setSelectedTenants([]);
    } else {
      setSelectedTenants(filteredTenants.map((t: Tenant) => t.id));
    }
  };

  const getResourceUsagePercentage = (used: number, limit: number) => {
    if (limit === -1) return 0; // Unlimited
    return Math.min((used / limit) * 100, 100);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Tenant Management</h2>
          <p className="text-gray-400 mt-1">
            Manage client instances and provisioning
          </p>
        </div>
        <CosmicButton
          onClick={() => setShowProvisioningWizard(true)}
          className="bg-gradient-to-r from-purple-600 to-pink-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Provision New Tenant
        </CosmicButton>
      </div>

      {/* Active Provisioning Jobs */}
      {provisioningJobs && provisioningJobs.length > 0 && (
        <CosmicCard className="border-blue-500/20 bg-blue-500/5">
          <div className="p-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              Active Provisioning Jobs
            </h3>
            <div className="space-y-3">
              {provisioningJobs.map((job: ProvisioningJob) => {
                const tenant = tenants?.find((t: Tenant) => t.id === job.tenant_id);
                const currentStep = job.steps.find((s: any) => s.status === 'in_progress');
                const completedSteps = job.steps.filter((s: any) => s.status === 'completed').length;
                const totalSteps = job.steps.length;
                const progress = (completedSteps / totalSteps) * 100;

                return (
                  <div key={job.id} className="bg-black/20 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-medium text-white">
                          {tenant?.name || 'Unknown Tenant'}
                        </h4>
                        <p className="text-sm text-gray-400">
                          {currentStep ? `${currentStep.name}...` : 'Preparing...'}
                        </p>
                      </div>
                      <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Step {completedSteps + 1} of {totalSteps}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </CosmicCard>
      )}

      {/* Filters and Search */}
      <div className="flex gap-4">
        <div className="flex-1">
          <CosmicInput
            placeholder="Search by name or subdomain..."
            value={searchTerm}
            onChange={(value: string) => setSearchTerm(value)}
            icon={<Search className="w-4 h-4" />}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-black/50 border border-purple-500/20 rounded-lg px-4 py-2 text-white focus:border-purple-500/50"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
        <CosmicButton
          variant="secondary"
          onClick={() => refetch()}
        >
          <RefreshCw className="w-4 h-4" />
        </CosmicButton>
      </div>

      {/* Bulk Actions */}
      {selectedTenants.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4 flex items-center justify-between"
        >
          <span className="text-white">
            {selectedTenants.length} tenant{selectedTenants.length > 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <CosmicButton
              variant="secondary"
              size="sm"
                            onClick={() => bulkActionMutation.mutate({
                action: 'suspend',
                tenantIds: selectedTenants
              } as any)}
            >
              Suspend
            </CosmicButton>
            <CosmicButton
              variant="secondary"
              size="sm"
                            onClick={() => bulkActionMutation.mutate({
                action: 'activate',
                tenantIds: selectedTenants
              } as any)}
            >
              Activate
            </CosmicButton>
            <CosmicButton
              variant="secondary"
              size="sm"
              className="text-red-400 hover:text-red-300"
              onClick={() => {
                if (confirm('Are you sure you want to delete these tenants?')) {
                                    bulkActionMutation.mutate({
                    action: 'delete',
                    tenantIds: selectedTenants
                  } as any);
                }
              }}
            >
              Delete
            </CosmicButton>
          </div>
        </motion.div>
      )}

      {/* Tenants Table */}
      <CosmicCard>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="p-4 text-left">
                  <input
                    type="checkbox"
                    checked={selectedTenants.length === filteredTenants.length && filteredTenants.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-600 bg-black/50"
                  />
                </th>
                <th className="p-4 text-left text-gray-400 font-medium">Tenant</th>
                <th className="p-4 text-left text-gray-400 font-medium">Domain</th>
                <th className="p-4 text-left text-gray-400 font-medium">Plan</th>
                <th className="p-4 text-left text-gray-400 font-medium">Status</th>
                <th className="p-4 text-left text-gray-400 font-medium">Resources</th>
                <th className="p-4 text-left text-gray-400 font-medium">Created</th>
                <th className="p-4 text-left text-gray-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTenants.map((tenant: Tenant) => (
                <motion.tr
                  key={tenant.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="border-b border-gray-800 hover:bg-white/5"
                >
                  <td className="p-4">
                    <input
                      type="checkbox"
                      checked={selectedTenants.includes(tenant.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTenants([...selectedTenants, tenant.id]);
                        } else {
                          setSelectedTenants(selectedTenants.filter(id => id !== tenant.id));
                        }
                      }}
                      className="rounded border-gray-600 bg-black/50"
                    />
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{tenant.name}</p>
                        <p className="text-sm text-gray-400">{tenant.id.slice(0, 8)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-white">{tenant.subdomain}.cosmkdrm.com</p>
                        {tenant.custom_domain && (
                          <p className="text-sm text-gray-400">{tenant.custom_domain}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${planColors[tenant.plan_type as keyof typeof planColors]}`}>
                      {tenant.plan_type.toUpperCase()}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {statusIcons[(tenant.provisioning_status === 'completed' ? tenant.status : tenant.provisioning_status) as keyof typeof statusIcons]}
                      <span className="text-white capitalize">
                        {tenant.provisioning_status === 'completed' ? tenant.status : tenant.provisioning_status}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    {tenant.resource_usage && tenant.limits && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <HardDrive className="w-3 h-3 text-gray-400" />
                          <div className="flex-1">
                            <div className="w-24 bg-gray-800 rounded-full h-1.5">
                              <div 
                                className="bg-gradient-to-r from-blue-500 to-purple-500 h-1.5 rounded-full"
                                style={{ width: `${getResourceUsagePercentage(tenant.resource_usage.storage_gb, tenant.limits.storage_gb)}%` }}
                              />
                            </div>
                          </div>
                          <span className="text-xs text-gray-400">
                            {tenant.resource_usage.storage_gb}/{tenant.limits.storage_gb === -1 ? '∞' : tenant.limits.storage_gb}GB
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-400">
                            {tenant.resource_usage.users}/{tenant.limits.users === -1 ? '∞' : tenant.limits.users} users
                          </span>
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="p-4">
                    <p className="text-white">{format(new Date(tenant.created_at), 'MMM d, yyyy')}</p>
                    <p className="text-sm text-gray-400">{format(new Date(tenant.created_at), 'h:mm a')}</p>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <CosmicButton
                        variant="secondary"
                        size="sm"
                        onClick={() => window.location.href = `/admin/tenants/${tenant.id}`}
                      >
                        View
                      </CosmicButton>
                      <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                        <MoreVertical className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </CosmicCard>

      {/* Provisioning Wizard Modal */}
      {showProvisioningWizard && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Provision New Tenant</h3>
            {/* Wizard component will be loaded here */}
            <p className="text-gray-400">Provisioning wizard component will be loaded here...</p>
            <div className="mt-6 flex justify-end">
              <CosmicButton
                variant="secondary"
                onClick={() => setShowProvisioningWizard(false)}
              >
                Close
              </CosmicButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};